import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import Layout from '../components/Layout';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Edit, Trash2, DollarSign, Package } from 'lucide-react';
import { formatNumberES } from '@/lib/utils';

export const Productos = () => {
  const { API, token } = useContext(AuthContext);
  const [productos, setProductos] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  
  const [formData, setFormData] = useState({
    nombre: '',
    tipo: 'individual',
    duracion_minutos: 60,
    precio: 0,
    max_participantes: 1,
    costes_variables: [],
    limite_min: 0,
    limite_max: 10,
    categoria: '',
    tiempo_preparacion_minutos: 0,
    tipo_preparacion: 'por_imparticion',
    iva_producto: 21.0,
    precio_incluye_iva: false,
    dias_cobro: 0
  });

  const [nuevoCosto, setNuevoCosto] = useState({ 
    nombre: '', 
    valor: 0, 
    tipo: 'fijo',
    iva: 21.0,
    incluye_iva: false,
    dias_pago: 30
  });

  useEffect(() => {
    cargarProductos();
  }, []);

  const cargarProductos = async () => {
    try {
      const response = await axios.get(`${API}/productos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProductos(response.data);
    } catch (error) {
      toast.error('Error al cargar productos');
    }
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      tipo: 'individual',
      duracion_minutos: 60,
      precio: 0,
      max_participantes: 1,
      costes_variables: [],
      limite_min: 0,
      limite_max: 10,
      categoria: '',
      tiempo_preparacion_minutos: 0,
      tipo_preparacion: 'por_imparticion',
      iva_producto: 21.0,
      precio_incluye_iva: false,
      dias_cobro: 0
    });
    setEditando(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editando) {
        await axios.put(`${API}/productos/${editando}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Producto actualizado');
      } else {
        await axios.post(`${API}/productos`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Producto creado');
      }
      cargarProductos();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar producto');
    }
  };

  const handleEdit = (producto) => {
    setFormData(producto);
    setEditando(producto.id);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este producto?')) return;
    try {
      await axios.delete(`${API}/productos/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Producto eliminado');
      cargarProductos();
    } catch (error) {
      toast.error('Error al eliminar producto');
    }
  };

  const agregarCosto = () => {
    if (!nuevoCosto.nombre || nuevoCosto.valor <= 0) {
      toast.error('Completa los datos del costo');
      return;
    }
    setFormData({
      ...formData,
      costes_variables: [...formData.costes_variables, {
        ...nuevoCosto,
        iva: nuevoCosto.iva || 21.0,
        incluye_iva: nuevoCosto.incluye_iva || false,
        dias_pago: nuevoCosto.dias_pago || 30
      }]
    });
    setNuevoCosto({ 
      nombre: '', 
      valor: 0, 
      tipo: 'fijo',
      iva: 21.0,
      incluye_iva: false,
      dias_pago: 30
    });
  };

  const eliminarCosto = (index) => {
    setFormData({
      ...formData,
      costes_variables: formData.costes_variables.filter((_, i) => i !== index)
    });
  };

  return (
    <Layout>
      <div className="page-header">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="page-title" data-testid="productos-title">Productos / Servicios</h1>
            <p className="page-subtitle">Gestiona tus clases, talleres y servicios educativos</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={resetForm} data-testid="btn-nuevo-producto">
                <Plus size={18} /> Nuevo Producto
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editando ? 'Editar Producto' : 'Nuevo Producto'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nombre</Label>
                    <Input
                      data-testid="input-nombre"
                      value={formData.nombre}
                      onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Tipo</Label>
                    <Select value={formData.tipo} onValueChange={(val) => setFormData({ ...formData, tipo: val })}>
                      <SelectTrigger data-testid="select-tipo">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="individual">Individual</SelectItem>
                        <SelectItem value="grupal">Grupal</SelectItem>
                        <SelectItem value="pack">Pack</SelectItem>
                        <SelectItem value="taller">Taller</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Duración (min)</Label>
                    <Input
                      data-testid="input-duracion"
                      type="number"
                      value={formData.duracion_minutos}
                      onChange={(e) => setFormData({ ...formData, duracion_minutos: parseInt(e.target.value) })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Precio (€)</Label>
                    <Input
                      data-testid="input-precio"
                      type="number"
                      step="0.01"
                      value={formData.precio}
                      onChange={(e) => setFormData({ ...formData, precio: parseFloat(e.target.value) })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Max Participantes</Label>
                    <Input
                      data-testid="input-participantes"
                      type="number"
                      value={formData.max_participantes}
                      onChange={(e) => setFormData({ ...formData, max_participantes: parseInt(e.target.value) })}
                      required
                    />
                  </div>
                </div>

                <div className="border-t pt-4">
                  <Label className="text-base font-semibold mb-3 block">IVA y Cobros</Label>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>IVA del Producto (%)</Label>
                      <Input
                        data-testid="input-iva-producto"
                        type="number"
                        step="0.1"
                        value={formData.iva_producto}
                        onChange={(e) => setFormData({ ...formData, iva_producto: parseFloat(e.target.value) || 0 })}
                        placeholder="21"
                      />
                      <p className="text-xs text-gray-500">0% = exento</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Días hasta Cobro</Label>
                      <Input
                        data-testid="input-dias-cobro"
                        type="number"
                        value={formData.dias_cobro}
                        onChange={(e) => setFormData({ ...formData, dias_cobro: parseInt(e.target.value) || 0 })}
                        placeholder="0"
                      />
                      <p className="text-xs text-gray-500">0 = inmediato</p>
                    </div>
                    <div className="space-y-2 flex items-end">
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="precio-incluye-iva"
                          checked={formData.precio_incluye_iva}
                          onChange={(e) => setFormData({ ...formData, precio_incluye_iva: e.target.checked })}
                          className="w-4 h-4"
                        />
                        <Label htmlFor="precio-incluye-iva" className="cursor-pointer">
                          El precio incluye IVA
                        </Label>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <Label className="text-base font-semibold mb-3 block">Tiempo de Preparación</Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Tiempo Preparación (min)</Label>
                      <Input
                        data-testid="input-preparacion"
                        type="number"
                        value={formData.tiempo_preparacion_minutos}
                        onChange={(e) => setFormData({ ...formData, tiempo_preparacion_minutos: parseInt(e.target.value) || 0 })}
                        placeholder="0"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Tipo de Preparación</Label>
                      <Select value={formData.tipo_preparacion} onValueChange={(val) => setFormData({ ...formData, tipo_preparacion: val })}>
                        <SelectTrigger data-testid="select-tipo-preparacion">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="por_imparticion">Por cada impartición</SelectItem>
                          <SelectItem value="unico">Única vez (primera sesión)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    El tiempo de preparación se suma al tiempo de trabajo. Si es "única vez", solo se cuenta en la primera sesión.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Categoría</Label>
                  <Input
                    data-testid="input-categoria"
                    value={formData.categoria}
                    onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                    placeholder="Ej: Yoga, Música, Idiomas..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Límite Mínimo (unidades)</Label>
                    <Input
                      data-testid="input-limite-min"
                      type="number"
                      value={formData.limite_min}
                      onChange={(e) => setFormData({ ...formData, limite_min: parseInt(e.target.value) })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Límite Máximo (unidades)</Label>
                    <Input
                      data-testid="input-limite-max"
                      type="number"
                      value={formData.limite_max}
                      onChange={(e) => setFormData({ ...formData, limite_max: parseInt(e.target.value) })}
                      required
                    />
                  </div>
                </div>

                <div className="border-t pt-4">
                  <Label className="text-base font-semibold">Costes Variables</Label>
                  <p className="text-sm text-gray-500 mt-1 mb-3">
                    Los costes pueden ser <strong>fijos</strong> (por unidad), <strong>por hora</strong> (según duración) o <strong>porcentaje</strong> (del precio del producto).
                  </p>
                  <div className="mt-3 space-y-3">
                    {formData.costes_variables.map((costo, index) => {
                      const mostrarValor = costo.tipo === "porcentaje" 
                        ? `${formatNumberES(costo.valor)}%` 
                        : `${formatNumberES(costo.valor)}€`;
                      const tipoTexto = costo.tipo === "fijo" ? "Fijo" 
                        : costo.tipo === "por_hora" ? "Por Hora" 
                        : "Porcentaje";
                      const costoCalculado = costo.tipo === "porcentaje" && formData.precio > 0
                        ? ` (${formatNumberES((formData.precio * formData.max_participantes * costo.valor) / 100)}€ por unidad)`
                        : "";
                      const ivaInfo = costo.iva !== undefined && costo.iva > 0
                        ? ` | IVA: ${formatNumberES(costo.iva)}%`
                        : "";
                      const diasPagoInfo = costo.dias_pago !== undefined && costo.dias_pago > 0
                        ? ` | Pago: ${costo.dias_pago}d`
                        : "";
                      
                      return (
                        <div key={index} className="flex items-center gap-2 bg-gray-50 p-3 rounded" data-testid={`costo-item-${index}`}>
                          <span className="flex-1">{costo.nombre}</span>
                          <span className="font-semibold">{mostrarValor}</span>
                          <span className="text-sm text-gray-500">({tipoTexto}){costoCalculado}{ivaInfo}{diasPagoInfo}</span>
                          <Button type="button" size="sm" variant="destructive" onClick={() => eliminarCosto(index)} data-testid={`btn-eliminar-costo-${index}`}>
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      );
                    })}
                    
                    <div className="space-y-3">
                      <div className="grid grid-cols-12 gap-2 items-end">
                        <div className="col-span-4">
                          <Input
                            data-testid="input-costo-nombre"
                            placeholder="Nombre"
                            value={nuevoCosto.nombre}
                            onChange={(e) => setNuevoCosto({ ...nuevoCosto, nombre: e.target.value })}
                          />
                        </div>
                        <div className="col-span-2">
                          <Input
                            data-testid="input-costo-valor"
                            type="number"
                            step={nuevoCosto.tipo === "porcentaje" ? "0.1" : "0.01"}
                            placeholder={nuevoCosto.tipo === "porcentaje" ? "Ej: 15" : nuevoCosto.tipo === "por_hora" ? "€/hora" : "€"}
                            value={nuevoCosto.valor}
                            onChange={(e) => setNuevoCosto({ ...nuevoCosto, valor: parseFloat(e.target.value) || 0 })}
                          />
                        </div>
                        <div className="col-span-2">
                          <Select value={nuevoCosto.tipo} onValueChange={(val) => setNuevoCosto({ ...nuevoCosto, tipo: val })}>
                            <SelectTrigger data-testid="select-costo-tipo">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="fijo">Fijo</SelectItem>
                              <SelectItem value="por_hora">Por Hora</SelectItem>
                              <SelectItem value="porcentaje">Porcentaje del Precio</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <Button type="button" size="sm" onClick={agregarCosto} data-testid="btn-agregar-costo">
                          <Plus size={14} />
                        </Button>
                      </div>
                      <div className="grid grid-cols-12 gap-2 items-end">
                        <div className="col-span-3">
                          <Label className="text-xs">IVA (%)</Label>
                          <Input
                            type="number"
                            step="0.1"
                            min="0"
                            value={nuevoCosto.iva !== undefined && nuevoCosto.iva !== null ? nuevoCosto.iva : 21.0}
                            onChange={(e) => {
                              const valor = e.target.value === '' ? 0 : parseFloat(e.target.value);
                              setNuevoCosto({ ...nuevoCosto, iva: isNaN(valor) ? 0 : valor });
                            }}
                            placeholder="21"
                          />
                        </div>
                        <div className="col-span-3">
                          <Label className="text-xs">Días hasta Pago</Label>
                          <Input
                            type="number"
                            min="0"
                            value={nuevoCosto.dias_pago !== undefined && nuevoCosto.dias_pago !== null ? nuevoCosto.dias_pago : 30}
                            onChange={(e) => {
                              const valor = e.target.value === '' ? 0 : parseInt(e.target.value);
                              setNuevoCosto({ ...nuevoCosto, dias_pago: isNaN(valor) ? 0 : valor });
                            }}
                            placeholder="30"
                          />
                        </div>
                        <div className="col-span-6 flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id="costo-incluye-iva"
                            checked={nuevoCosto.incluye_iva || false}
                            onChange={(e) => setNuevoCosto({ ...nuevoCosto, incluye_iva: e.target.checked })}
                            className="w-4 h-4"
                          />
                          <Label htmlFor="costo-incluye-iva" className="cursor-pointer text-xs">
                            El coste incluye IVA
                          </Label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button type="submit" className="flex-1" data-testid="btn-guardar-producto">
                    {editando ? 'Actualizar' : 'Crear'} Producto
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} data-testid="btn-cancelar">
                    Cancelar
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="page-body">
        <div className="grid gap-4">
          {productos.length === 0 ? (
            <Card className="card text-center py-12" data-testid="productos-empty">
              <Package size={48} className="mx-auto text-gray-400 mb-4" />
              <h3 className="text-xl font-semibold mb-2">No hay productos configurados</h3>
              <p className="text-gray-600 mb-4">Comienza creando tu primer producto o servicio</p>
              <Button onClick={() => setDialogOpen(true)} data-testid="btn-crear-primero">
                <Plus size={18} /> Crear Primer Producto
              </Button>
            </Card>
          ) : (
            productos.map((producto) => (
              <Card key={producto.id} className="card" data-testid={`producto-card-${producto.id}`}>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-bold">{producto.nombre}</h3>
                      <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                        {producto.tipo}
                      </span>
                    </div>
                    {producto.categoria && (
                      <p className="text-sm text-gray-500 mb-3">{producto.categoria}</p>
                    )}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-4">
                      <div>
                        <p className="text-sm text-gray-500">Duración</p>
                        <p className="font-semibold">{producto.duracion_minutos} min</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Preparación</p>
                        <p className="font-semibold">
                          {producto.tiempo_preparacion_minutos || 0} min
                          {producto.tiempo_preparacion_minutos > 0 && (
                            <span className="text-xs block text-gray-500">
                              ({producto.tipo_preparacion === 'unico' ? 'única' : 'c/u'})
                            </span>
                          )}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Precio</p>
                        <p className="font-semibold">{formatNumberES(producto.precio)}€</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Participantes</p>
                        <p className="font-semibold">{producto.max_participantes}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Límites</p>
                        <p className="font-semibold">{producto.limite_min} - {producto.limite_max}</p>
                      </div>
                    </div>
                    {producto.costes_variables.length > 0 && (
                      <div className="mt-4 pt-4 border-t">
                        <p className="text-sm font-semibold text-gray-700 mb-2">Costes Variables:</p>
                        <div className="flex flex-wrap gap-2">
                          {producto.costes_variables.map((costo, idx) => {
                            const mostrarValor = costo.tipo === "porcentaje" 
                              ? `${formatNumberES(costo.valor)}%` 
                              : `${formatNumberES(costo.valor)}€`;
                            const tipoTexto = costo.tipo === "fijo" ? "Fijo" 
                              : costo.tipo === "por_hora" ? "Por Hora" 
                              : "Porcentaje";
                            return (
                              <span key={idx} className="px-3 py-1 bg-gray-100 rounded text-sm">
                                {costo.nombre}: {mostrarValor} ({tipoTexto})
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => handleEdit(producto)} data-testid={`btn-edit-${producto.id}`}>
                      <Edit size={16} />
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => handleDelete(producto.id)} data-testid={`btn-delete-${producto.id}`}>
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Productos;
